import type { RadioProps as MuiRadioProps } from '@mui/material/Radio';
import type { SxProps, Theme } from '@mui/material/styles';

import MuiFormControlLabel from '@mui/material/FormControlLabel';
import MuiRadio from '@mui/material/Radio';
import { Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';

import type { FieldProps } from '../types';

import { HelperMessage } from '../../HelperMessage';
import { ErrorMessage } from '../../ErrorMessage';

export type RadioFieldProps = Pick<
  MuiRadioProps,
  'classes' | 'name' | 'onChange' | 'value'
> & {
  sx?: SxProps<Theme>;
} & Omit<FieldProps, 'name'>;

const RadioField = React.forwardRef(
  (
    {
      className,
      classes,
      'data-testid': testId,
      disabled,
      errors,
      helperText,
      label,
      loading,
      name,
      onChange,
      sx,
      value,
    }: RadioFieldProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    if (loading) {
      return (
        <Skeleton
          sx={{ display: 'flex', flexDirection: 'column', px: 4 }}
          className={className}
          height={42}
        />
      );
    }
    return (
      <Box
        flexDirection="column"
        className={className}
        display="flex"
        sx={sx}
        px={4}
      >
        <MuiFormControlLabel
          control={
            <MuiRadio
              data-testid={testId}
              disabled={disabled}
              onChange={onChange}
              classes={classes}
              name={name}
              ref={ref}
            />
          }
          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
          value={value}
          label={label}
        />
        {helperText && (
          <HelperMessage sx={{ pl: '31px', pr: 2 }}>{helperText}</HelperMessage>
        )}
        {errors && (
          <Box pl="10px" pr={2}>
            <ErrorMessage errors={errors} />
          </Box>
        )}
      </Box>
    );
  },
);

RadioField.displayName = 'CheckboxField';

export default RadioField;
